import { useEffect, useMemo } from 'react';
import {
  useGetConversationalWritingRuleQuery,
  useLazyGetConversationalWritingRuleQuery,
} from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import { RuleResponse } from 'store/api/apiTypes';

/**
 * Hook to fetch Conversational Writing visibility rule data for the currently selected project domain.
 * Provides both cached automatic query behavior and a manual trigger (lazy) for on-demand refresh.
 */
export const useConversationalWritingRule = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );

  const activeReport = useAppSelector(state => state.history.activeReport);
  const requestBody = useMemo(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain: selectedProject.domain, reportId: activeReport?.id };
  }, [selectedProject, activeReport]);

  const {
    data: ruleData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetConversationalWritingRuleQuery(
    requestBody as { domain: string; reportId?: string },
    {
      skip: !requestBody,
      // Re-fetch when selected project changes or active report changes
      refetchOnMountOrArgChange: true,
    }
  );

  const [trigger, lazyResult] = useLazyGetConversationalWritingRuleQuery();

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  // Handle errors from primary query
  useEffect(() => {
    if (error) {
      handleApiError(error);
    }
  }, [error, handleApiError]);

  usePollingOnError(requestBody, error, refetch, ruleData);

  const typed = ruleData as RuleResponse | undefined;
  const companyAssessment = typed?.company?.assessment_explanation || '';
  const productKeys = typed ? Object.keys(typed.products || {}) : [];

  return {
    ruleData: typed,
    companyAssessment,
    productKeys,
    isLoading,
    isFetching,
    error,
    hasData: !!ruleData,
    refetch,
    lazyTrigger: (domainOverride?: string) =>
      trigger({ domain: domainOverride || selectedProject?.domain || '' }),
    lazyResult,
    showErrorModal,
    hideErrorModal,
    handleRetry,
  };
};
